import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { settledList } from '../../utils/loadState'
import { getCustomer, getAnalysisHistory, getAnalysisModes } from '../../api'
import './AnalysisHistory.css'

export default function AnalysisHistory() {
  const { customerId } = useParams()
  const [customer, setCustomer] = useState(null)
  const [history, setHistory] = useState([])
  const [modes, setModes] = useState([])
  const [filterMode, setFilterMode] = useState('')
  const [loading, setLoading] = useState(true)

  // A failed load is an error with a Retry, never "No analyses yet" (item 201).
  const [historyError, setHistoryError] = useState(null)

  async function load() {
    setLoading(true)
    try {
      const [ctx, hist, md] = await Promise.allSettled([
        getCustomer(customerId),
        getAnalysisHistory(customerId),
        getAnalysisModes(),
      ])
      if (ctx.status === 'fulfilled') setCustomer(ctx.value)
      // GET /api/analyze/history answers {entries: [...]}; this page used to read only
      // analyses/history, so it showed "No analyses yet" whatever the server held.
      const hl = settledList(hist, 'entries', 'analyses', 'history')
      setHistory(hl.items || [])
      setHistoryError(hl.error)
      if (md.status === 'fulfilled') {
        setModes(md.value?.modes || (Array.isArray(md.value) ? md.value : []))
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [customerId])

  const filtered = history.filter(a => {
    if (!filterMode) return true
    return a.mode === filterMode
  })

  const companyName = customer?.company_name || customerId

  const modeColors = {
    file_compare: 'var(--blue)',
    variance_analysis: 'var(--amber)',
    compliance_check: 'var(--red)',
    gap_analysis: 'var(--purple)',
    config_audit: 'var(--green)',
    trend_analysis: 'var(--blue)',
    schema_compare: 'var(--gray-500)',
  }

  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: companyName, href: `/customers/${customerId}` },
    { label: 'Analysis History' },
  ]
  const navActions = (
    <>
      <Link to={`/customers/${customerId}/work-efforts/new`} className="nav-btn primary">New Work Effort</Link>
      
    </>
  )

  if (loading) {
    return (
      <>
        <TopNav breadcrumbs={breadcrumbs} actions={navActions} />
        <div className="ah-loading"><div className="ah-spinner" /><p>Loading analysis history…</p></div>
      </>
    )
  }

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} actions={navActions} />

      <div className="ah-page">
        {/* Header */}
        <div className="ah-header">
          <div>
            <h1 className="ah-title">Analysis History</h1>
            <p className="ah-subtitle">{history.length} analyses for {companyName}</p>
          </div>
          <div className="ah-controls">
            <select className="ah-select" value={filterMode} onChange={e => setFilterMode(e.target.value)}>
              <option value="">All modes</option>
              {modes.map(m => (
                <option key={m.mode} value={m.mode}>{m.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Modes Info */}
        {modes.length > 0 && (
          <div className="ah-modes">
            {modes.map(m => (
              <div key={m.mode} className="ah-mode-chip" style={{ borderColor: modeColors[m.mode] || 'var(--gray-300)' }}>
                <span className="ah-mode-dot" style={{ background: modeColors[m.mode] || 'var(--gray-400)' }} />
                <span className="ah-mode-name">{m.name}</span>
                <span className="ah-mode-desc">{m.description}</span>
              </div>
            ))}
          </div>
        )}

        {/* Analysis List */}
        {historyError ? (
          <LoadError what="analyses" error={historyError} onRetry={load} />
        ) : filtered.length === 0 ? (
          <div className="ah-empty">
            <div className="ah-empty-icon">📊</div>
            <h3>{history.length === 0 ? 'No analyses yet' : 'No matches for this filter'}</h3>
            <p>Run analyses from the Work Effort workspace to see results here.</p>
            <Link to={`/customers/${customerId}/work-efforts/new`} className="ah-cta">Start New Work Effort →</Link>
          </div>
        ) : (
          <div className="ah-list">
            {filtered.map((a, i) => (
              <div key={i} className="ah-card">
                <div className="ah-card-left">
                  <span
                    className="ah-card-mode"
                    style={{ background: modeColors[a.mode] || 'var(--gray-400)' }}
                  >
                    {(a.mode || 'analysis').replace(/_/g, ' ')}
                  </span>
                  <div className="ah-card-info">
                    <div className="ah-card-title">{a.title || a.mode?.replace(/_/g, ' ') || 'Analysis'}</div>
                    <div className="ah-card-meta">
                      {a.created_at ? new Date(a.created_at).toLocaleString() : ''}
                      {a.finding_count !== undefined && ` · ${a.finding_count} findings`}
                      {a.row_count !== undefined && ` · ${a.row_count} records`}
                    </div>
                  </div>
                </div>
                <div className="ah-card-right">
                  {a.match_rate !== undefined && (
                    <div className="ah-card-rate">{(a.match_rate * 100).toFixed(0)}% match</div>
                  )}
                  <span className={`ah-card-status ${a.status || 'complete'}`}>{a.status || 'complete'}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )
}
